import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight, CheckCircle2, Rocket, Code, Bot, Gamepad2, Globe,
  Brain, Cpu, Sparkles, ChevronRight, Star, GraduationCap, Award
} from 'lucide-react';

const curriculumPaths = [
  {
    level: 'Explorer',
    color: 'from-green-400 to-emerald-500',
    bgLight: 'bg-green-50 border-green-200',
    textColor: 'text-green-700',
    ages: 'Ages 5-7 (Grades K-2)',
    icon: Sparkles,
    description: 'First steps into the world of coding through visual block programming.',
    courses: [
      { title: 'Scratch Jr. Adventures', topics: ['Block coding', 'Animations', 'Storytelling'], duration: '10 weeks' },
    ],
    skills: ['Sequencing', 'Pattern Recognition', 'Logical Thinking', 'Creativity'],
    badge: 'Explorer Badge',
  },
  {
    level: 'Builder',
    color: 'from-blue-400 to-blue-600',
    bgLight: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-700',
    ages: 'Ages 8-10 (Grades 3-5)',
    icon: Code,
    description: 'Building games and projects with Scratch and introductory text coding.',
    courses: [
      { title: 'Scratch Game Design', topics: ['Game mechanics', 'Sprites', 'Variables'], duration: '12 weeks' },
      { title: 'Robotics with LEGO SPIKE', topics: ['Sensors', 'Motors', 'Engineering'], duration: '10 weeks' },
    ],
    skills: ['Variables', 'Conditionals', 'Loops', 'Debugging', 'Teamwork'],
    badge: 'Builder Badge',
  },
  {
    level: 'Coder',
    color: 'from-purple-400 to-purple-600',
    bgLight: 'bg-purple-50 border-purple-200',
    textColor: 'text-purple-700',
    ages: 'Ages 11-13 (Grades 6-8)',
    icon: Cpu,
    description: 'Transitioning to real programming languages and building functional applications.',
    courses: [
      { title: 'Python Fundamentals', topics: ['Syntax', 'Functions', 'Data structures'], duration: '14 weeks' },
      { title: 'Web Development Basics', topics: ['HTML', 'CSS', 'JavaScript'], duration: '12 weeks' },
      { title: 'Minecraft Modding with Java', topics: ['Java', 'Object-oriented', 'Game mods'], duration: '10 weeks' },
    ],
    skills: ['Text Coding', 'Functions', 'Data Structures', 'Web Design', 'Problem Solving'],
    badge: 'Coder Badge',
  },
  {
    level: 'Innovator',
    color: 'from-orange-400 to-red-500',
    bgLight: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-700',
    ages: 'Ages 14-18 (Grades 9-12)',
    icon: Brain,
    description: 'Advanced projects in AI, data science, game development, and competitive programming.',
    courses: [
      { title: 'Advanced Python & Data Science', topics: ['Pandas', 'Visualization', 'ML basics'], duration: '14 weeks' },
      { title: 'Game Development with Unity', topics: ['C#', 'Game physics', '3D modeling'], duration: '16 weeks' },
    ],
    skills: ['AI/ML Basics', 'Data Analysis', 'Game Development', 'Software Architecture', 'Collaboration'],
    badge: 'Innovator Badge',
  },
];

export default function CurriculumPage() {
  const [expandedLevel, setExpandedLevel] = useState(null);

  return (
    <div data-testid="curriculum-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6">
              <GraduationCap className="w-4 h-4" /> Best In STEM™ — Top 5% Accredited Curriculum
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              Your Child's Coding<br />
              <span className="text-rsc-yellow">Learning Path</span>
            </h1>
            <p className="text-xl text-white/80 mb-8">
              From first blocks to advanced AI — our structured curriculum grows with your child 
              through four progressive levels, each building on the last.
            </p>
            <Link to="/catalog">
              <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                Browse All Courses <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Path Overview */}
      <section className="py-12 bg-white border-b">
        <div className="container-app">
          <div className="flex flex-wrap justify-center gap-4">
            {curriculumPaths.map((path, i) => {
              const Icon = path.icon;
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${path.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-rsc-navy">{path.level}</p>
                    <p className="text-xs text-gray-500">{path.ages}</p>
                  </div>
                  {i < curriculumPaths.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-gray-300 mx-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Curriculum Levels */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Four Levels of Mastery</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Each level is designed with age-appropriate challenges, projects, and assessments 
              to keep students engaged and progressing.
            </p>
          </div>

          <div className="space-y-8">
            {curriculumPaths.map((path, i) => {
              const Icon = path.icon;
              const isExpanded = expandedLevel === i;
              return (
                <Card 
                  key={i} 
                  className={`overflow-hidden border-2 transition-all duration-300 cursor-pointer ${isExpanded ? path.bgLight : 'hover:shadow-lg'}`}
                  onClick={() => setExpandedLevel(isExpanded ? null : i)}
                  data-testid={`curriculum-level-${i}`}
                >
                  <CardContent className="p-0">
                    {/* Level Header */}
                    <div className="flex items-center gap-6 p-6 md:p-8">
                      <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${path.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading text-2xl md:text-3xl font-bold text-rsc-navy">Level {i + 1}: {path.level}</h3>
                          <Badge className={`${path.textColor} bg-white border`}>{path.ages}</Badge>
                        </div>
                        <p className="text-gray-600">{path.description}</p>
                      </div>
                      <div className="hidden md:flex items-center gap-2">
                        <Award className={`w-8 h-8 ${path.textColor}`} />
                        <span className={`font-semibold text-sm ${path.textColor}`}>{path.badge}</span>
                      </div>
                      <ChevronRight className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t p-6 md:p-8 bg-white/80">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {/* Courses */}
                          <div>
                            <h4 className="font-heading text-lg font-bold text-rsc-navy mb-4">Courses in This Level</h4>
                            <div className="space-y-3">
                              {path.courses.map((course, ci) => (
                                <div key={ci} className="p-4 bg-gray-50 rounded-xl">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-semibold text-rsc-navy">{course.title}</h5>
                                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">{course.duration}</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {course.topics.map((topic, ti) => (
                                      <Badge key={ti} variant="outline" className="text-xs">{topic}</Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* Skills */}
                          <div>
                            <h4 className="font-heading text-lg font-bold text-rsc-navy mb-4">Skills Your Child Will Learn</h4>
                            <div className="space-y-2">
                              {path.skills.map((skill, si) => (
                                <div key={si} className="flex items-center gap-2">
                                  <CheckCircle2 className={`w-5 h-5 ${path.textColor}`} />
                                  <span className="text-gray-700">{skill}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border">
                              <div className="flex items-center gap-3">
                                <Award className={`w-10 h-10 ${path.textColor}`} />
                                <div>
                                  <p className="font-bold text-rsc-navy">{path.badge}</p>
                                  <p className="text-sm text-gray-600">Earned upon completing all courses in this level</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 text-center">
                          <Link to="/catalog">
                            <Button className="btn-primary">
                              View {path.level} Courses <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Our Curriculum */}
      <section className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Why Our Curriculum Stands Out</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">Built by educators, refined through years of teaching, and accredited by STEM.org.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Star, title: 'STEM.org Accredited', desc: 'Our curriculum meets the highest standards of STEM education, verified by independent accreditation.' },
              { icon: Gamepad2, title: 'Gamified Learning', desc: 'Points, badges, and rewards keep students motivated. Belt progression tracks advancement.' },
              { icon: Rocket, title: 'Project-Based', desc: 'Every course culminates in real projects students can share — games, websites, robots, and apps.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-rsc-yellow/20 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-rsc-yellow" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-white/70 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-rsc-blue/5 to-rsc-yellow/5">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-6">Ready to Start Your Child's Journey?</h2>
          <p className="text-gray-600 text-lg mb-8 max-w-xl mx-auto">
            Book a free trial class and discover which level is right for your child.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                Book a Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/catalog">
              <Button variant="outline" className="text-lg px-8 py-6">Browse All Courses</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
